import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  LandingRefusal,
  landingPagesService,
  type LandingDraftHistory,
  type LandingDraftSummary,
  type LandingPreviewRequest,
} from '../services/landingPagesService';

/**
 * What this client actually sells, and where. The form used to carry
 * hardcoded placeholders — "Kitchen Remodeling" while you were looking at a
 * fencing client — which read as suggestions for the wrong business.
 */
export const useLandingSuggestions = (tenantId: string | null) =>
  useQuery({
    queryKey: ['landingPages', 'suggestions', tenantId],
    queryFn: () => landingPagesService.suggestions(tenantId as string),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });

/**
 * Campaigns, ad groups and the keywords an ad group targets.
 *
 * Why this exists at all: `service_name` was free text, and that is where two
 * live defects entered — "SEO Services" reduced to no identifying words, so
 * the hero-relevance gate could not evaluate and passed the page unchecked;
 * and a comma-joined name makes that gate demand the union of every service in
 * the string. Keywords from an ad group are already client-approved and
 * already singular.
 *
 * Progressive by design: pass a campaign to get its ad groups AND the pooled
 * keywords of every one of them, plus the service name and vertical those
 * imply. The backend returns whichever levels are answerable, so the UI never
 * has to know which query serves which question.
 *
 * adGroup narrows to a single group. It is no longer part of the normal flow —
 * the campaign is the unit — and exists to resolve a campaign whose ad groups
 * derive different services.
 */
export const useLandingAdContext = (
  tenantId: string | null,
  campaign?: string | null,
  adGroup?: string | null,
) =>
  useQuery({
    queryKey: ['landingPages', 'adContext', tenantId, campaign ?? '', adGroup ?? ''],
    queryFn: () =>
      landingPagesService.adContext(tenantId as string, campaign, adGroup),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    // A client with no ads account answers available:false rather than
    // failing, so a retry buys nothing and costs a Google API call.
    retry: false,
  });

export const useLandingTemplates = () =>
  useQuery({
    queryKey: ['landingPages', 'templates'],
    queryFn: () => landingPagesService.templates(),
    staleTime: 5 * 60_000,
  });

/**
 * Preview is a mutation, not a query: it costs an LLM call and fetches and
 * re-encodes imagery, so it runs when an operator asks and never on render
 * or refocus.
 */
export const useLandingPreview = () =>
  useMutation({
    mutationFn: (req: LandingPreviewRequest) => landingPagesService.preview(req),
    retry: false, // a refusal is an answer; retrying just asks again
  });

/** History for the current tenant. A query, not a mutation: it reads stored
 *  rows and costs nothing, so it may refetch. Invalidated after a generation
 *  so a new run appears in the list without a manual reload. */
export const useLandingDrafts = (
  tenantId: string,
  pageType = 'landing',
  enabled = true
) =>
  useQuery({
    queryKey: ['page-builder-drafts', tenantId, pageType],
    queryFn: () => landingPagesService.listDrafts(tenantId, pageType),
    enabled: enabled && !!tenantId,
    staleTime: 30_000,
  });

/** One stored run, fetched only when an operator opens it — the HTML is
 *  ~150KB and the list shows up to 200 rows. */
export const useLandingDraft = (tenantId: string, draftId: string | null) =>
  useQuery({
    queryKey: ['landing-draft', tenantId, draftId],
    queryFn: () => landingPagesService.getDraft(tenantId, draftId as string),
    enabled: !!tenantId && !!draftId,
    staleTime: Infinity, // a stored run never changes
  });

export const useLandingPublish = () =>
  useMutation({
    mutationFn: (req: LandingPreviewRequest & { draft_id?: string | null }) =>
      landingPagesService.publish(req),
    retry: false,
  });

const DSH_POLL_MS = 15_000;
const DSH_TICK_MS = 1_000;
const DSH_GIVE_UP_MS = 30 * 60_000;

export const DSH_MEDIAN_MS = 788_000;

const runsIn = (history: LandingDraftHistory): LandingDraftSummary[] =>
  history.groups.flatMap((g) => g.dates.flatMap((d) => d.runs));

const instantOf = (iso: string): number =>
  Date.parse(/([Zz]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);

const layoutEngineOf = (copyMeta: unknown): string | null => {
  const v = (copyMeta as { layout_engine?: unknown } | null)?.layout_engine;
  return typeof v === 'string' ? v : null;
};

export const useLandingDshRun = (tenantId: string) => {
  const [runId, setRunId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [readyDraftId, setReadyDraftId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const running = runId !== null;

  const stop = () => {
    setRunId(null);
    setStartedAt(null);
  };

  useEffect(() => {
    if (startedAt === null) return;
    const id = window.setInterval(
      () => setElapsedMs(Date.now() - startedAt),
      DSH_TICK_MS,
    );
    return () => window.clearInterval(id);
  }, [startedAt]);

  useEffect(() => {
    if (runId === null || startedAt === null || !tenantId) return;
    let cancelled = false;

    const look = async () => {
      if (Date.now() - startedAt > DSH_GIVE_UP_MS) {
        stop();
        setError(
          'No page after 30 minutes. The run may still be working — check History before starting another.',
        );
        return;
      }
      let row: LandingDraftSummary | undefined;
      try {
        const history = await landingPagesService.listDrafts(tenantId);
        row = runsIn(history).find(
          (r) => r.run_id === runId && instantOf(r.created_at) > startedAt,
        );
      } catch {
        return;
      }
      if (!row || cancelled) return;

      try {
        const draft = await queryClient.fetchQuery({
          queryKey: ['landing-draft', tenantId, row.id],
          queryFn: () => landingPagesService.getDraft(tenantId, row!.id),
          staleTime: Infinity,
        });
        if (cancelled) return;
        const engine = layoutEngineOf(draft.copy_meta);
        if (engine !== null && engine !== 'dsh') {
          stop();
          setError(
            `The run came back from the ${engine} engine, not the DeepSeek lane. Nothing was lost — the page is in History — but the lane is not wired the way this screen believes.`,
          );
          return;
        }
      } catch {
        return;
      }

      stop();
      queryClient.invalidateQueries({
        queryKey: ['page-builder-drafts', tenantId, 'landing'],
      });
      setReadyDraftId(row.id);
    };

    const id = window.setInterval(look, DSH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runId, startedAt, tenantId, queryClient]);

  const start = (req: LandingPreviewRequest) => {
    setError(null);
    setReadyDraftId(null);
    setElapsedMs(0);
    setRunId(req.run_id ?? null);
    setStartedAt(Date.now());
    landingPagesService.preview(req).catch((err: unknown) => {
      if (err instanceof LandingRefusal) {
        stop();
        setError(err.detail);
      }
    });
  };

  const cancel = () => {
    stop();
    setReadyDraftId(null);
    setError(null);
    setElapsedMs(0);
  };

  return { running, elapsedMs, readyDraftId, error, start, cancel };
};

export { LandingRefusal };
